import { useState } from 'react';
import { Search, Loader2, ImageOff, ExternalLink } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import api from '@/lib/api';

/**
 * Search Unsplash / Pexels / Pixabay and drop the result straight into a slot.
 *
 * The service behind this has existed for a long time with no way for a person
 * to reach it — three keyed providers, ranked pooling and full licence
 * compliance, used only by background jobs. This is the picker.
 *
 * Attribution comes back with the import and is handed to `onPick` alongside
 * the URL. Callers that store it (homepage sections keep it in
 * `content.image_meta`) get a rendered photo credit for free; the credit is a
 * licence obligation for Unsplash, not a nicety.
 */

export interface StockPickResult {
  url: string;
  image_meta: {
    source: string;
    photographer: string | null;
    photographer_url: string | null;
    page_url: string | null;
    query: string;
    picked_at: string;
    verified: boolean;
  };
}

interface StockCandidate {
  url: string;
  source: string;
  photographer: string | null;
  photographer_url: string | null;
  page_url: string | null;
  width: number | null;
  height: number | null;
  query: string;
}

export interface StockSearchProps {
  onPick: (result: StockPickResult) => void;
  /** Where the import lands, so keys stay tidy. */
  folder?: 'homepage' | 'blog' | 'cms';
  /** Crop target passed to the importer; omit to keep the original aspect. */
  targetWidth?: number;
  targetHeight?: number;
  /** Seed the search box — usually the slot's label or the section name. */
  initialQuery?: string;
}

/**
 * The search-and-import body, with no dialog around it.
 *
 * Split out so `<AssetPicker>` can host it as a tab. Nesting the modal version
 * inside another modal stacks two focus traps and two Escape handlers, and the
 * inner one's close button dismisses the wrong dialog.
 */
export function StockPhotoSearch({
  onPick,
  folder = 'cms',
  targetWidth,
  targetHeight,
  initialQuery = '',
}: StockSearchProps) {
  const [query, setQuery] = useState(initialQuery);
  const [searched, setSearched] = useState('');
  const [results, setResults] = useState<StockCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);
  const [error, setError] = useState('');

  async function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<{ results: StockCandidate[] }>(
        `/admin/stock-photos/search?q=${encodeURIComponent(q)}`,
      );
      setResults(res.results);
      setSearched(q);
    } catch (err) {
      // The "no providers configured" case is a 503 whose message names the fix
      // (add a key under Credentials). Surface it verbatim rather than replacing
      // it with a generic failure, because it is an operator action, not a dead
      // end the designer can do anything about.
      setError(err instanceof Error && err.message ? err.message : 'Search failed. Try again in a moment.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  async function pick(candidate: StockCandidate) {
    setImporting(candidate.url);
    setError('');
    try {
      const res = await api.post<StockPickResult>('/admin/stock-photos/import', {
        url: candidate.url,
        query: candidate.query,
        folder,
        ...(targetWidth ? { width: targetWidth } : {}),
        ...(targetHeight ? { height: targetHeight } : {}),
      });
      onPick(res);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not import that photo.');
    } finally {
      setImporting(null);
    }
  }

  return (
    <>
      <form onSubmit={runSearch} className="flex gap-2 mb-4">
        <input
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
          placeholder="What should the photo show?"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <Button type="submit" disabled={loading || query.trim().length < 2}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Search className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="ml-2">Search</span>
        </Button>
      </form>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && searched && results.length === 0 && !error && (
        <div className="py-10 text-center text-gray-500">
          <ImageOff className="h-7 w-7 mx-auto mb-2 text-gray-300" aria-hidden="true" />
          Nothing for “{searched}”. Try a simpler, more literal description.
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto">
            {results.map((candidate) => (
              <button
                key={candidate.url}
                type="button"
                onClick={() => pick(candidate)}
                disabled={!!importing}
                className="group relative overflow-hidden rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sage-500 disabled:opacity-50"
                title={
                  candidate.photographer
                    ? `Photo by ${candidate.photographer} on ${candidate.source}`
                    : candidate.source
                }
              >
                <img
                  src={candidate.url}
                  alt=""
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition group-hover:scale-105"
                />
                {importing === candidate.url && (
                  <span className="absolute inset-0 grid place-items-center bg-black/40">
                    <Loader2 className="h-6 w-6 animate-spin text-white" aria-hidden="true" />
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-left text-[11px] text-white">
                  {candidate.photographer ?? 'Unknown'} · {candidate.source}
                </span>
              </button>
            ))}
          </div>
          {/* Not decoration: Unsplash's terms require the photographer credit to
              be shown wherever the photo is used, and it is stored on import so
              the storefront can render it. */}
          <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            The photographer credit is saved with the image and shown on the site.
          </p>
        </>
      )}
    </>
  );
}

/** The same search, in its own dialog — how every existing caller uses it. */
export default function StockPhotoPicker({
  isOpen,
  onClose,
  ...rest
}: StockSearchProps & { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Find a stock photo" className="max-w-3xl">
      <StockPhotoSearch
        {...rest}
        onPick={(result) => {
          rest.onPick(result);
          onClose();
        }}
      />
    </Modal>
  );
}
